import React, { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import {
    Box, Tooltip,
    Grid,
    Button,
} from "@mui/material";
import { calendarIcon, crossIcon, } from "./icons";
import { formatSeasonDateRange, getUsedDateFromSeasons, filterSeasons, itIsPricingModel3Data } from './function';
import { DateRangePicker } from "react-date-range";
import moment from "moment";
import { deepCopy } from "../../../app/util";

const DateRangePickerInput = ({ season, seasonEditCopyRef, manageSeasonChangeFunc, isReadOnlyMode, expanded }) => {
    const { app_state, ui_state } = useSelector(state => state.app.current_state);
    const { as_of_date } = app_state.primary_stay_range;
    const { isDefault, plan_period } = seasonEditCopyRef.current;
    const pricingSeasons = itIsPricingModel3Data(app_state.pricingSeasons);
    const pricingSeasonsUI = deepCopy(ui_state.pricingSeasonsUI);
    const { activeSeasons } = filterSeasons(pricingSeasons);
    const { discardCount, openItemId, tabPanelIndex } = pricingSeasonsUI;

    const [dateRangePickerVisible, setDateRangePickerVisible] = useState(false);
    const [fromDate, setFromDate] = useState(new Date(plan_period.effective_date_range.from));
    const [untilDate, setUntilDate] = useState(new Date(plan_period.effective_date_range.until));

    // Disabled Dates
    const findUsedDate = tabPanelIndex === 0 ? activeSeasons : []; // to avoid eslint warning
    const { usedDate, useDateYYYYMMDD } = getUsedDateFromSeasons(findUsedDate, season);
    // Check if a given day is disabled
    const isDisabledDate = (date) => useDateYYYYMMDD.some((d) => moment(date).format('YYYY-MM-DD') === d);

    const toggleDateRangePickerVisibility = (visible) => {
        setDateRangePickerVisible(visible)
    };


    const handleCloseBtnClick = (e) => {
        e.stopPropagation();
        setFromDate(new Date(plan_period.effective_date_range.from));
        setUntilDate(new Date(plan_period.effective_date_range.until));
        toggleDateRangePickerVisibility(false);
    }
    const handleApplyBtnClick = (fromDate, untilDate, e) => {
        e.stopPropagation();
        seasonEditCopyRef.current.plan_period.effective_date_range.from = moment(fromDate).format('YYYY-MM-DD');
        seasonEditCopyRef.current.plan_period.effective_date_range.until = moment(untilDate).format('YYYY-MM-DD');
        manageSeasonChangeFunc({ action: 'inputChange', season: seasonEditCopyRef.current });
        toggleDateRangePickerVisibility(false);
    }

    useEffect(() => {
        setFromDate(new Date(plan_period.effective_date_range.from));
        setUntilDate(new Date(plan_period.effective_date_range.until));
    }, [discardCount]);

    useEffect(() => {
        if (openItemId !== season.id) {
            setDateRangePickerVisible(false);
        }
        // if new item then open date picker
        if (openItemId && openItemId.toString().includes('new_')) {
            toggleDateRangePickerVisibility(true);
        }
    }, [openItemId]);

    return (
        <Grid className={`date-range-section ${dateRangePickerVisible && expanded ? 'date-range-picker-visible' : ''}`} >
            {!isDefault && (
                <Box sx={{ height: "100%", display: "flex", alignItems: "center", gap: "12px" }} onClick={() => { !isReadOnlyMode && toggleDateRangePickerVisibility(true) }}  >
                    {calendarIcon}
                    <Box sx={{ height: "100%" }}>
                        <Box className="summary-label date-range-label">Date Range</Box>
                        <Box className="summary-value" >
                            <Box className={`
                                    ${!isReadOnlyMode && expanded ?
                                    `date-range-value ${false ? 'error' : ''}`
                                    :
                                    ""
                                }
                                `}
                            >
                                {formatSeasonDateRange(
                                    plan_period.effective_date_range.from,
                                    plan_period.effective_date_range.until
                                )}
                            </Box>
                        </Box>
                    </Box>

                    {/* Date Picker Popup Container */}
                    <Box className={`date-range-picker-container`} >
                        { expanded && dateRangePickerVisible && 
                            (
                                <div className='date-range-picker-inner'>
                                    <Box className="datepicker-header">
                                        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                            {calendarIcon}
                                            <div className='dh-date-range-label'>Date Range</div>
                                        </Box>
                                        <div className="cross-icon" onClick={(e) => handleCloseBtnClick(e)}>{crossIcon}</div>
                                    </Box>
                                    {dateRangePickerVisible && (
                                        <DateRangePicker
                                            showSelectionPreview={true}
                                            moveRangeOnFirstSelection={false}
                                            months={2}
                                            direction="horizontal"
                                            className=""
                                            ranges={[
                                                {
                                                    startDate: new Date(fromDate),
                                                    endDate: new Date(untilDate),
                                                    key: "selection",
                                                },
                                            ]}
                                            minDate={new Date(as_of_date)} // disable past dates
                                            onChange={(ranges) => {
                                                setFromDate(ranges.selection.startDate);
                                                setUntilDate(ranges.selection.endDate);
                                            }}
                                            disabledDates={usedDate}
                                            disabledDay={isDisabledDate} // custom disable dates
                                            dayContentRenderer={(date) => { // add tooltip on custom disabled dates
                                                const disabled = isDisabledDate(date);
                                                // find season name from date
                                                let seasonHaveDate = activeSeasons.find((s) => {
                                                    if (s.isDefault) {
                                                        return false;
                                                    }
                                                    const dateItem = moment(date);
                                                    const fromDate = moment(s.plan_period.effective_date_range.from);
                                                    const untilDate = moment(s.plan_period.effective_date_range.until);
                                                    const isInRange = dateItem.isBetween(fromDate, untilDate, null, '[]');
                                                    return isInRange;
                                                })

                                                if (seasonHaveDate && disabled) {
                                                    return (
                                                        <Tooltip
                                                            arrow
                                                            placement="top"
                                                            title={`You can't select this date because it's already included in ${seasonHaveDate.plan_name}`}
                                                        >
                                                            <span className="blackout">
                                                                {date.getDate()}
                                                            </span>
                                                        </Tooltip>
                                                    )
                                                }
                                                else {
                                                    return (
                                                        <span>
                                                            {date.getDate()}
                                                        </span>
                                                    )
                                                }
                                            }}
                                        />
                                    )}
                                    <Box className="datepicker-footer">
                                        <Button className="datepicker-apply-btn" onClick={(e) => handleApplyBtnClick(fromDate, untilDate, e)} >
                                            Apply
                                        </Button>
                                    </Box>
                                </div>
                            )}
                    </Box>
                </Box>
            )}
        </Grid>
    )
}

export default DateRangePickerInput